import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import abPartyLogo from '../assets/ab_party_logo_300x300.jpg';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL } from '../services/api/config';
import { getJoinFormUrl, getVolunteerFormUrl } from '../utils/formUrls';

const SPECIAL_TITLES = [
  { bn: 'Mission & Vision', en: 'Mission & Vision' },
  { bn: 'Affiliated Organization', en: 'Affiliated Organization' },
];

const FIXED_SLUGS = {
  "Mission & Vision": "mission-vision",
  "Affiliated Organization": "affiliated-organization",
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const bottomNavRef = useRef(null);
  const [dropdownTop, setDropdownTop] = useState(0);
  const { language, toggleLanguage } = useLanguage();
  // Downloadable policy paper in public folder (URL-encoded path for spaces)
  const policyPaperUrl = '/Policy%20Paper%201%20-%20Youth%20Unemployment%20-%2023%20July.pdf';
  const [userContents, setUserContents] = useState([]);
  // Mobile accordion state
  const [mobileOpen, setMobileOpen] = useState({
    campaigns: false,
    updates: false,
    about: false,
    policies: false,
    shadow: false,
    abPeople: false,
    press: false,
  });
  const toggleMobileSection = (key) => setMobileOpen(prev => ({ ...prev, [key]: !prev[key] }));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMoreOpen && !event.target.closest('.nav-item') && !event.target.closest('.more-dropdown-content')) {
        setIsMoreOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMoreOpen]);

  // Compute dropdown top position so it overlays the entire screen below the nav
  useEffect(() => {
    const computeTop = () => {
      if (bottomNavRef.current) {
        const rect = bottomNavRef.current.getBoundingClientRect();
        setDropdownTop(rect.bottom);
      }
    };
    computeTop();
    window.addEventListener('resize', computeTop);
    window.addEventListener('scroll', computeTop, { passive: true });
    return () => {
      window.removeEventListener('resize', computeTop);
      window.removeEventListener('scroll', computeTop);
    };
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/articles?limit=1000`)
      .then(res => res.json())
      .then(data => setUserContents(data.articles));
  }, []);

  const getUserContentArticle = (bn, en) => userContents.find(a => a.title === bn || a.titleEn === en);
  const getSlugByTitle = (bn, en) => {
    const article = getUserContentArticle(bn, en);
    return article ? article.slug : null;
  };

  // Slugs for policy/ideology pages (fall back to fixed slugs if missing)
  const slugBangladesh2 = getSlugByTitle('Bangladesh 2.0', 'Bangladesh 2.0') || 'bangladesh-2-0';
  const slugSecondRepublic = getSlugByTitle('Second Republic', 'Second Republic') || 'second-republic';
  const slugManifesto = getSlugByTitle('Manifesto', 'Manifesto') || 'the-manifesto';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Top Header Bar - Sticky */}
      <header className="bg-white text-gray-800 shadow-sm sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-2 flex justify-between items-center min-h-[56px] sm:min-h-[60px]">
          {/* Logo and Party Name */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center">
              <img
                loading="lazy"
                src={abPartyLogo}
                alt="AB Party Logo"
                className="h-12 w-auto"
              />
            </Link>
            <div className="text-xl font-bold text-primary">
              {language === 'english' ? 'AB Party' : 'এবি পার্টি'}
            </div>
          </div>

          {/* Action Buttons and Language Toggle */}
          <div className="hidden md:flex items-center gap-3">
            <a href={getJoinFormUrl(language)} target="_blank" rel="noopener noreferrer" className="bg-primary text-white px-4 py-2 rounded-full font-medium flex items-center gap-2 hover:bg-primary-dark transition-colors text-sm">
              {language === 'english' ? 'Join →' : 'জয়েন →'}
            </a>
            <Link to="/donate" className="bg-red-600 text-white px-4 py-2 rounded-full font-medium flex items-center gap-2 hover:bg-red-700 transition-colors text-sm">
              {language === 'english' ? 'Donate →' : 'ডোনেট →'}
            </Link>
            {/* Language Toggle */}
            <div
              className="flex h-8 w-20 cursor-pointer items-center overflow-hidden rounded-full text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              onClick={toggleLanguage}
            >
              <span className={`flex h-full w-1/2 items-center justify-center transition-all duration-300 ${language === 'bengali' ? 'bg-primary' : 'bg-gray-300'}`}>অ</span>
              <span className={`flex h-full w-1/2 items-center justify-center transition-all duration-300 ${language === 'english' ? 'bg-secondary' : 'bg-gray-300'}`}>A</span>
            </div>
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center gap-2 md:gap-3 md:hidden">
            <div
              className="flex h-8 w-20 cursor-pointer items-center overflow-hidden rounded-full text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              onClick={toggleLanguage}
            >
              <span className={`flex h-full w-1/2 items-center justify-center transition-all duration-300 ${language === 'bengali' ? 'bg-primary' : 'bg-gray-300'}`}>অ</span>
              <span className={`flex h-full w-1/2 items-center justify-center transition-all duration-300 ${language === 'english' ? 'bg-secondary' : 'bg-gray-300'}`}>A</span>
            </div>
            <button className="p-2 bg-transparent border-none cursor-pointer text-gray-800" onClick={toggleMobileMenu}>
              <div className="flex flex-col gap-1">
                <span className="w-6 h-0.5 bg-gray-800 rounded transition-all"></span>
                <span className="w-6 h-0.5 bg-gray-800 rounded transition-all"></span>
                <span className="w-6 h-0.5 bg-gray-800 rounded transition-all"></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden ${isMobileMenuOpen ? 'flex' : 'hidden'} flex-col bg-white p-3 border-t border-gray-200 gap-2 max-h-[80vh] overflow-y-auto`}>
          <a href={getJoinFormUrl(language)} target="_blank" rel="noopener noreferrer" className="w-full bg-primary text-white px-4 py-2 rounded-full font-medium flex items-center justify-center gap-2 hover:bg-primary-dark transition-colors text-sm" onClick={() => setIsMobileMenuOpen(false)}>
            {language === 'english' ? 'Join →' : 'জয়েন →'}
          </a>
          <Link to="/donate" className="w-full bg-red-600 text-white px-4 py-2 rounded-full font-medium flex items-center justify-center gap-2 hover:bg-red-700 transition-colors text-sm" onClick={() => setIsMobileMenuOpen(false)}>
            {language === 'english' ? 'Donate →' : 'ডোনেট →'}
          </Link>
          <a href={getVolunteerFormUrl(language)} target="_blank" rel="noopener noreferrer" className="w-full bg-white text-secondary border border-gray-300 px-4 py-2 rounded-full font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors text-sm" onClick={() => setIsMobileMenuOpen(false)}>
            {language === 'english' ? 'Volunteer →' : 'স্বেচ্ছাসেবক →'}
          </a>

          <div className="border-t border-gray-200 pt-2 mt-2">
            {/* Campaigns Section (Accordion) */}
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base"
              onClick={() => toggleMobileSection('campaigns')}
            >
              <span>{language === 'english' ? 'Campaigns' : 'ক্যাম্পেইন'}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileOpen.campaigns ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen.campaigns ? 'max-h-80' : 'max-h-0'}`}>
              <Link to="/volunteer-opportunities" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Learn about campaigning' : 'ক্যাম্পেইন সম্পর্কে জানুন'}</Link>
              <a href={getVolunteerFormUrl(language)} target="_blank" rel="noopener noreferrer" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Join as a volunteer' : 'স্বেচ্ছাসেবক হোন'}</a>
              <Link to="/candidates-map" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Find a campaign near you' : 'ক্যাম্পেইন খুঁজুন'}</Link>
              <Link to="/donate" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Fund our campaigns' : 'ক্যাম্পেইনের জন্য ফান্ড সংগ্রহ করুন'}</Link>
            </div>

            {/* Policies & Ideology Section (Accordion) */}
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base"
              onClick={() => toggleMobileSection('policies')}
            >
              <span>{language === 'english' ? 'Policies & Ideology' : 'নীতি ও আদর্শ'}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileOpen.policies ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen.policies ? 'max-h-60' : 'max-h-0'}`}>
              <Link to="/bangladesh-2-0" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Bangladesh 2.0' : 'বাংলাদেশ ২.০'}</Link>
              <Link to="/second-republic" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Second Republic' : 'সেকেন্ড রিপাবলিক'}</Link>
              <Link to={`/articles/${slugManifesto}`} className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Manifesto' : 'ম্যানিফেস্টো'}</Link>
            </div>

            {/* AB People Section */}
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base"
              onClick={() => toggleMobileSection('abPeople')}
            >
              <span>{language === 'english' ? 'AB People' : 'এবি পিপল'}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileOpen.abPeople ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen.abPeople ? 'max-h-40' : 'max-h-0'}`}>
              <Link to="/leadership" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'AB Leadership' : 'এবি নেতৃত্ব'}</Link>
              <Link to="/ab-wings" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'AB Wings' : 'এবি উইংস'}</Link>
            </div>

            {/* Updates Section (Accordion) */}
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base"
              onClick={() => toggleMobileSection('updates')}
            >
              <span>{language === 'english' ? 'Updates' : 'আপডেট'}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileOpen.updates ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen.updates ? 'max-h-60' : 'max-h-0'}`}>
              <Link to="/updates/latest" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Latest' : 'সর্বশেষ'}</Link>
              <Link to="/updates/featured" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Featured' : 'ফিচার্ড '}</Link>
              <Link to="/updates/notice-board" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Notice Board' : 'নোটিশ বোর্ড'}</Link>
            </div>

            {/* Press Releases Section (Accordion) */}
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base"
              onClick={() => toggleMobileSection('press')}
            >
              <span>{language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ'}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileOpen.press ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen.press ? 'max-h-40' : 'max-h-0'}`}>
              <Link to="/press-releases" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ'}</Link>
            </div>

            {/* About Us Section (Accordion) */}
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base"
              onClick={() => toggleMobileSection('about')}
            >
              <span>{language === 'english' ? 'About Us' : 'আমাদের সম্পর্কে'}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileOpen.about ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen.about ? 'max-h-60' : 'max-h-0'}`}>
              <Link to={`/articles/${getSlugByTitle('Mission & Vision', 'Mission & Vision') || 'mission-vision'}`} className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Mission & Vision' : 'মিশন ও ভিশন'}</Link>
              <Link to={`/articles/${getSlugByTitle('Affiliated Organization', 'Affiliated Organization') || 'affiliated-organization'}`} className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Affiliated Organization' : 'সম্পৃক্ত সংস্থা'}</Link>
              <Link to="/ab-wings" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'AB Wings & Committees' : 'এবি উইংস ও কমিটি'}</Link>
              <Link to="/contact" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Contact' : 'যোগাযোগ'}</Link>
            </div>

            {/* Shadow Cabinet (Accordion) */}
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base"
              onClick={() => toggleMobileSection('shadow')}
            >
              <span>{language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট '}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileOpen.shadow ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen.shadow ? 'max-h-60' : 'max-h-0'}`}>
              <Link to="/articles/shadow-cabinet" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট '}</Link>
              <a href={policyPaperUrl} download className="block w-full text-left py-2 pl-2 text-primary font-medium text-sm" onClick={() => setIsMobileMenuOpen(false)}>
                {language === 'english' ? 'Download Policy Paper' : 'নীতিপত্র ডাউনলোড করুন'}
              </a>
              <Link to="/shop" className="block w-full text-left py-2 pl-2 text-gray-600 text-sm" onClick={() => setIsMobileMenuOpen(false)}>{language === 'english' ? 'Shop' : 'Shop'}</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Bottom Navigation Bar - Non-sticky (hidden on mobile) */}
      <nav className="bg-white border-t border-gray-200 hidden md:block">
        <div className="max-w-[1400px] mx-auto px-2" ref={bottomNavRef}>
          <div className="flex items-center space-x-6 py-1 overflow-x-auto overflow-y-visible">

            {/* 1. Join AB Party */}
            <a href={getJoinFormUrl(language)} target="_blank" rel="noopener noreferrer" className="text-gray-700 font-medium hover:text-primary transition-colors text-sm whitespace-nowrap">
              {language === 'english' ? 'Join AB Party' : 'জয়েন এবি পার্টি'}
            </a>

            {/* 2. AB People */}
            <Link to="/leadership" className="text-gray-700 font-medium hover:text-primary transition-colors text-sm whitespace-nowrap">
              {language === 'english' ? 'AB leadership' : 'এবি নেতৃত্ব'}
            </Link>

            {/* 3. Shop */}
            <Link to="/shop" className="text-gray-700 font-medium hover:text-primary transition-colors text-sm whitespace-nowrap">
              {language === 'english' ? 'Shop' : 'শপ'}
            </Link>

            {/* 4. More Dropdown - Mega menu */}
            <div className="relative nav-item more-dropdown-container">

              <div
                className="flex items-center gap-1 text-gray-700 font-medium hover:text-primary transition-colors cursor-pointer text-sm whitespace-nowrap select-none"
                onClick={() => {
                  const newState = !isMoreOpen;
                  setIsMoreOpen(newState);
                }}
              >
                <span>{language === 'english' ? 'More' : 'আরও'}</span>
                <svg className={`w-3 h-3 transition-transform ${isMoreOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Overlay to prevent background interaction and avoid visual overlap */}
              {isMoreOpen && (
                <div
                  className="fixed left-0 right-0 bottom-0 bg-black/10 z-30"
                  style={{ top: dropdownTop }}
                  onClick={() => setIsMoreOpen(false)}
                />
              )}
            </div>
            {/* Large Dropdown Menu with smooth animation */}
            <div
              className={`more-dropdown-content fixed left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 transition-all duration-300 ease-out ${isMoreOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-2 invisible pointer-events-none'} max-h-[80vh] overflow-y-auto`}
              style={{ top: dropdownTop }}
            >
              <div className="max-w-[1400px] mx-auto px-6 py-8">
                <div className="grid grid-cols-1 md:grid-cols-7 gap-8 items-start">

                  {/* Column 1: Campaigns */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      {language === 'english' ? 'Campaigns' : 'ক্যাম্পেইন'}
                    </h3>
                    <ul className="space-y-2 text-left">
                      <li><Link to="/volunteer-opportunities" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Learn about campaigning' : 'ক্যাম্পেইন সম্পর্কে জানুন'}</Link></li>
                      <li><a href={getVolunteerFormUrl(language)} target="_blank" rel="noopener noreferrer" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Join as a volunteer' : 'স্বেচ্ছাসেবক হোন'}</a></li>
                      <li><Link to="/candidates-map" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Find a campaign near you' : 'ক্যাম্পেইন খুঁজুন'}</Link></li>
                      <li><Link to="/donate" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Fund our campaigns' : 'ক্যাম্পেইনের জন্য ফান্ড সংগ্রহ করুন'}</Link></li>
                    </ul>
                  </div>

                  {/* Column 2: Policies & Ideology AB People */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      {language === 'english' ? 'Policies & Ideology' : 'নীতি ও আদর্শ'}
                    </h3>
                    <ul className="space-y-2 text-left">
                      <li><Link to="/bangladesh-2-0" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Bangladesh 2.0' : 'বাংলাদেশ ২.০'}</Link></li>
                      <li><Link to="/second-republic" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Second Republic' : 'সেকেন্ড রিপাবলিক'}</Link></li>
                      <li><Link to={`/articles/${slugManifesto}`} className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Manifesto' : 'ম্যানিফেস্টো'}</Link></li>
                    </ul>
                  </div>

                  {/* Column 3: AB People */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      {language === 'english' ? 'AB People' : 'এবি পিপল'}
                    </h3>
                    <ul className="space-y-2 text-left">
                      <li><Link to="/leadership" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'AB Leadership' : 'এবি নেতৃত্ব'}</Link></li>
                      <li><Link to="/ab-wings" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'AB Wings' : 'এবি উইংস'}</Link></li>
                    </ul>
                  </div>

                  {/* Column 4: Updates simplified */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      {language === 'english' ? 'Updates' : 'আপডেট'}
                    </h3>
                    <ul className="space-y-2 text-left">
                      <li><Link to="/updates/latest" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Latest' : 'সর্বশেষ'}</Link></li>
                      <li><Link to="/updates/featured" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Featured' : 'ফিচার্ড '}</Link></li>
                      <li><Link to="/updates/notice-board" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Notice Board' : 'নোটিশ বোর্ড'}</Link></li>
                    </ul>
                  </div>

                  {/* Column 5: Press Releases */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      {language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ'}
                    </h3>
                    <ul className="space-y-2 text-left">
                      <li><Link to="/press-releases" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ'}</Link></li>
                    </ul>
                  </div>

                  {/* Column 6: About Us using slugs */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      {language === 'english' ? 'About Us' : 'আমাদের সম্পর্কে'}
                    </h3>
                    <ul className="space-y-2 text-left">
                      <li><Link to={`/articles/${getSlugByTitle('Mission & Vision', 'Mission & Vision') || 'mission-vision'}`} className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Mission & Vision' : 'মিশন ও ভিশন'}</Link></li>
                      <li><Link to={`/articles/${getSlugByTitle('Affiliated Organization', 'Affiliated Organization') || 'affiliated-organization'}`} className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Affiliated Organization' : 'সম্পৃক্ত সংস্থা'}</Link></li>
                      <li><Link to="/ab-wings" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'AB Wings & Committees' : 'এবি উইংস ও কমিটি'}</Link></li>
                      <li><Link to="/contact" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>{language === 'english' ? 'Contact' : 'যোগাযোগ'}</Link></li>
                    </ul>
                  </div>

                  {/* Column 7: Shadow Cabinet */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      {language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট '}
                    </h3>
                    <ul className="space-y-2 text-left">
                      <li>
                        <Link to="/articles/shadow-cabinet" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>
                          {language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট '}
                        </Link>
                      </li>
                      <li>
                        <a
                          href={policyPaperUrl}
                          download
                          className="inline-flex items-center gap-1 text-sm text-secondary hover:underline"
                        >
                          {language === 'english' ? 'Download Policy Paper' : 'নীতিপত্র ডাউনলোড করুন'}
                          <svg className="w-3 h-3 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </a>
                      </li>
                      <li>
                        <Link to="/shop" className="inline-block text-gray-700 hover:text-primary transition-colors text-sm" onClick={() => setIsMoreOpen(false)}>
                          {language === 'english' ? 'Shop' : 'শপ'}
                        </Link>
                      </li>
                    </ul>
                  </div>

                </div>
                {/* Bottom action bar (Volunteer, Donate, Join) */}
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <div className="flex flex-wrap gap-6">
                    <a
                      href={getVolunteerFormUrl(language)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:text-red-700 transition-colors text-sm font-medium flex items-center gap-1"
                      onClick={() => setIsMoreOpen(false)}
                    >
                      {language === 'english' ? 'Volunteer' : 'স্বেচ্ছাসেবক'}
                      <span>→</span>
                    </a>
                    <Link
                      to="/donate"
                      className="text-secondary hover:text-red-700 transition-colors text-sm font-medium flex items-center gap-1"
                      onClick={() => setIsMoreOpen(false)}
                    >
                      {language === 'english' ? 'Donate' : 'ডোনেট'}
                      <span>→</span>
                    </Link>
                    <a
                      href={getJoinFormUrl(language)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:text-red-700 transition-colors text-sm font-medium flex items-center gap-1"
                      onClick={() => setIsMoreOpen(false)}
                    >
                      {language === 'english' ? 'Join' : 'জয়েন'}
                      <span>→</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav >

    </>
  );

};
export default Header; 